import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../../config/firebase';
import { ref, get } from 'firebase/database';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner } from '../ui/Spinner';
import {
  StudentBadge,
  StudentCard,
  StudentEmpty,
  StudentPage,
  StudentSection,
} from './StudentUI';
import { formatStudentDate } from '../../utils/studentExperience';

const StudentAssignments = () => {
  const { currentUser } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const assignmentsRef = ref(db, `assignments/${currentUser.uid}`);
        const snapshot = await get(assignmentsRef);
        if (snapshot.exists()) {
          setAssignments(Object.values(snapshot.val()));
        } else {
          setAssignments([]);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [currentUser.uid]);

  const stats = useMemo(() => {
    const completed = assignments.filter(
      (assignment) => assignment.status === 'completed',
    ).length;
    const pending = assignments.filter(
      (assignment) => assignment.status !== 'completed',
    ).length;

    return {
      total: assignments.length,
      completed,
      pending,
    };
  }, [assignments]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <StudentPage
      title="Assignments"
      subtitle="Keep track of the work that still needs your attention."
    >
      <StudentSection title="Overview">
        <div className="grid gap-4 sm:grid-cols-3">
          <SummaryCard label="All assignments" value={stats.total} />
          <SummaryCard label="To do" value={stats.pending} tone="warning" />
          <SummaryCard label="Completed" value={stats.completed} tone="success" />
        </div>
      </StudentSection>

      <StudentSection title="Assignment list">
        {!assignments.length ? (
          <StudentEmpty
            title="No assignments yet"
            message="New assignments will appear here when your instructor adds them."
          />
        ) : (
          <div className="grid gap-4">
            {assignments.map((assignment) => (
              <StudentCard key={assignment.id} className="bg-white">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-lg font-semibold text-stone-900">
                        {assignment.title}
                      </p>
                      <p className="mt-1 text-sm text-stone-500">
                        {assignment.courseName || 'Course assignment'}
                      </p>
                    </div>
                    <StudentBadge
                      tone={
                        assignment.status === 'completed' ? 'success' : 'warning'
                      }
                    >
                      {assignment.status === 'completed' ? 'Completed' : 'Pending'}
                    </StudentBadge>
                  </div>
                  {assignment.description ? (
                    <p className="text-sm leading-6 text-stone-600">
                      {assignment.description}
                    </p>
                  ) : null}
                  <div className="grid gap-3 md:grid-cols-3">
                    <InfoCard
                      label="Due date"
                      value={formatStudentDate(assignment.dueDate)}
                    />
                    <InfoCard
                      label="Submitted"
                      value={
                        assignment.submittedAt
                          ? formatStudentDate(assignment.submittedAt)
                          : 'Not yet'
                      }
                    />
                    <InfoCard
                      label="Score"
                      value={assignment.score ?? 'Waiting'}
                    />
                  </div>
                </div>
              </StudentCard>
            ))}
          </div>
        )}
      </StudentSection>
    </StudentPage>
  );
};

const SummaryCard = ({ label, value, tone = 'soft' }) => (
  <StudentCard className="bg-white">
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
        {label}
      </p>
      <div className="flex items-center justify-between gap-3">
        <p className="text-2xl font-semibold text-stone-900">{value}</p>
        <StudentBadge tone={tone}>{label}</StudentBadge>
      </div>
    </div>
  </StudentCard>
);

const InfoCard = ({ label, value }) => (
  <div className="rounded-2xl bg-stone-50 px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
      {label}
    </p>
    <p className="mt-1 text-sm font-semibold text-stone-900">{value}</p>
  </div>
);

export default StudentAssignments;
